/**
 * Serialize OPERATIONAL_TEMPLATE to ADL2 OPT2 (cADL + ODIN).
 */

import * as openehr_am from "../am/openehr_am.ts";
import { ADL2Serializer, type ADL2SerializerConfig } from "./adl2_serializer.ts";
import {
  serializeAnnotationsSection,
  serializeComponentTerminologiesSection,
  serializeDescriptionSection,
  serializeLanguageSection,
  serializeRmOverlaySection,
  serializeTerminologySection,
} from "../parser/odin_serializer.ts";
import type { OdinValue } from "../parser/odin_parser.ts";
import {
  getAnnotationsDocumentation,
  getRmOverlayVisibility,
} from "../parser/aom_odin_sections.ts";
import { serializeRulesSection } from "../parser/rules_serializer.ts";
import { withSyntheticNodeIdAnnotations } from "../parser/synthetic_node_annotations.ts";
import {
  archetypeTermBagsForLanguage,
  type OperationalTemplateWithTermScopes,
  resolveLocatableLabel,
  type TermScopeMeta,
  TERM_NAME_FALLBACK_NODE_ID_KEY,
} from "./term_scope.ts";
import { resolveTemplateLanguage } from "./term_codes.ts";
import { readTemplateId, type TermBag } from "../am/util/ontology_merge.ts";
import { filterOperationalTemplateLanguages } from "./opt2_trim.ts";
import {
  type Adl2CodingOptions,
  opt14NodeIdToAdl2,
  opt14TerminologyKeyToAdl2,
  resolveAdl2CodingSystem,
} from "../parser/legacy/opt14_code_mapping.ts";
import {
  adl2CodingSystemOf,
  adl2VersionFor,
} from "../parser/legacy/adl2_coding_system.ts";

/** Options for {@link OPT2Serializer.serializeOperationalTemplate}. */
export interface Opt2SerializeOptions extends Adl2CodingOptions {
  indent?: string;
  adlVersion?: string;
  rmRelease?: string;
  /**
   * Keep only these language codes in terminology / component_terminologies /
   * localized description and annotation bags (clone + filter before serialize).
   */
  languages?: string[];
  /**
   * Add human-readable `-- label` comments on cADL nodes using template /
   * component terminology (increases output size; does not remove ODIN sections).
   */
  inlineTermLabels?: boolean;
  /** Language for {@link Opt2SerializeOptions.inlineTermLabels}; defaults to first `languages` entry or template primary. */
  inlineTermLabelLanguage?: string;
  /**
   * Write a `synthetic_node_id` annotation for each minted node id, so a
   * re-parse knows to leave it out of data paths (default true).
   */
  syntheticNodeIdAnnotations?: boolean;
}

export interface CadlLabelContext {
  inlineLabels: boolean;
  resolveLabel(node: openehr_am.C_OBJECT): string | undefined;
}

export interface OPT2SerializerConfig extends ADL2SerializerConfig {
  labelContext?: CadlLabelContext;
}

/**
 * ADL2 serializer with OPT2 `operational_template` metadata sections.
 */
export class OPT2Serializer extends ADL2Serializer {
  private labelContext?: CadlLabelContext;
  private serializeCoding?: Adl2CodingOptions;

  constructor(config?: OPT2SerializerConfig) {
    super(config);
    this.labelContext = config?.labelContext;
  }

  setLabelContext(ctx: CadlLabelContext | undefined): void {
    this.labelContext = ctx;
  }

  /**
   * Serialize an operational template to ADL2 `operational_template` text.
   */
  serializeOperationalTemplate(
    opt: openehr_am.OPERATIONAL_TEMPLATE,
    options?: Opt2SerializeOptions,
  ): string {
    this.serializeCoding = options;
    const working = options?.languages?.length
      ? filterOperationalTemplateLanguages(opt, options.languages)
      : opt;

    if (options?.inlineTermLabels) {
      const language = resolveTemplateLanguage(
        working,
        options.inlineTermLabelLanguage ?? options.languages?.[0],
      );
      const templateTerms = ((working.ontology as {
        term_definitions?: Record<string, TermBag>;
      } | undefined)?.term_definitions?.[language] ?? {}) as TermBag;
      const archetypeTerms = archetypeTermBagsForLanguage(
        working as OperationalTemplateWithTermScopes,
        language,
      );
      this.setLabelContext({
        inlineLabels: true,
        resolveLabel: (node) =>
          resolveLocatableLabel(
            node.node_id,
            (node as TermScopeMeta)[TERM_NAME_FALLBACK_NODE_ID_KEY],
            templateTerms,
            archetypeTerms,
            (node as TermScopeMeta).term_archetype_scope,
          ),
      });
    } else {
      this.setLabelContext(undefined);
    }

    const indent = this.config.indent || "    ";
    let adl = "operational_template";
    // id-coded input stays id-coded; 1.4 / at-coded nodes are written in the
    // requested coding system (at-coded by default).
    const outCoding = adl2CodingSystemOf(working) === "id-coded"
      ? "id-coded"
      : resolveAdl2CodingSystem(options);
    const adlVer = options?.adlVersion ?? adl2VersionFor(outCoding, "2.0.5");
    const rmRel = options?.rmRelease ?? working.rm_release ?? "1.0.4";
    adl += ` (adl_version=${adlVer}; rm_release=${rmRel})`;
    adl += "\n";

    const rawTemplateId = readTemplateId(working) ?? working.archetype_id?.value;
    const templateId = rawTemplateId && adl2IdText(rawTemplateId);
    if (templateId) {
      adl += `    ${templateId}\n\n`;
    }

    const parentId = working.parent_archetype_id?.value;
    if (parentId) {
      adl += `specialize\n    ${parentId}\n\n`;
    }

    const translations = (working as { translations?: unknown }).translations;
    adl += serializeLanguageSection(
      working.original_language,
      indent,
      translations as OdinValue | undefined,
    );
    // An OPT 1.4 template_id that is not a valid ADL id (e.g. one with
    // spaces) is kept in description.other_details["template_id"].
    adl += serializeDescriptionSection(
      working.description,
      indent,
      rawTemplateId && templateId !== rawTemplateId ? { template_id: rawTemplateId } : undefined,
    );

    adl += "definition\n";
    if (working.definition) {
      adl += this.serializeCObject(working.definition, 1);
    }
    adl += "\n";

    adl += serializeRulesSection(working.invariants, indent);

    const { nodeIds, valueCodes } = collectNodeAndValueCodes(working.definition);
    const ontologyBag = working.ontology as Record<string, unknown> | undefined;
    adl += serializeTerminologySection(
      remapOntologyNodeIds(ontologyBag, nodeIds, valueCodes, options),
      indent,
    );

    const scoped = working as OperationalTemplateWithTermScopes;
    adl += serializeComponentTerminologiesSection(
      remapComponentTerminologies(
        scoped.archetype_term_definitions,
        nodeIds,
        valueCodes,
        options,
      ),
      indent,
    );

    const documentation = options?.syntheticNodeIdAnnotations === false
      ? getAnnotationsDocumentation(working)
      : withSyntheticNodeIdAnnotations(
        getAnnotationsDocumentation(working),
        working,
        (id) => this.formatNodeId(id),
      );
    adl += serializeAnnotationsSection(documentation, indent);
    adl += serializeRmOverlaySection(
      getRmOverlayVisibility(working),
      indent,
    );

    return adl;
  }

  protected override decorateObjectLine(
    node: openehr_am.C_OBJECT,
    line: string,
  ): string {
    if (!this.labelContext?.inlineLabels) return line;
    const label = this.labelContext.resolveLabel(node);
    if (!label) return line;
    const safe = label.replace(/\r?\n/g, " ").trim();
    return `${line}  -- ${safe}`;
  }
}

function collectNodeAndValueCodes(
  node: unknown,
  nodeIds = new Set<string>(),
  valueCodes = new Set<string>(),
  seen = new Set<object>(),
): { nodeIds: Set<string>; valueCodes: Set<string> } {
  if (!node || typeof node !== "object" || seen.has(node)) {
    return { nodeIds, valueCodes };
  }
  seen.add(node);
  const rec = node as Record<string, unknown>;
  if (typeof rec.node_id === "string" && rec.node_id) nodeIds.add(rec.node_id);
  if (node instanceof openehr_am.C_TERMINOLOGY_CODE) {
    if (node.constraint) valueCodes.add(node.constraint);
    const list = (node as { code_list?: string[] }).code_list;
    for (const code of list ?? []) valueCodes.add(code);
  }
  if (node instanceof openehr_am.C_ORDINAL) {
    const list = (node as {
      list?: Array<{ symbol?: { code_string?: string } }>;
    }).list;
    for (const item of list ?? []) {
      const code = item.symbol?.code_string;
      if (code) valueCodes.add(code);
    }
  }
  if (Array.isArray(rec.children)) {
    for (const child of rec.children) {
      collectNodeAndValueCodes(child, nodeIds, valueCodes, seen);
    }
  }
  if (Array.isArray(rec.attributes)) {
    for (const attr of rec.attributes) {
      collectNodeAndValueCodes(attr, nodeIds, valueCodes, seen);
    }
  }
  return { nodeIds, valueCodes };
}

function remapTermBag(
  bag: TermBag,
  nodeIds: Set<string>,
  valueCodes: Set<string>,
  coding?: Adl2CodingOptions,
): TermBag {
  const out: TermBag = {};
  for (const [code, entry] of Object.entries(bag)) {
    const mapped = opt14TerminologyKeyToAdl2(code, nodeIds, valueCodes, coding);
    const idCode = opt14NodeIdToAdl2(code, coding);
    const converts = idCode !== code && nodeIds.has(code);
    if (mapped !== code) {
      out[mapped] = { ...entry };
      if (converts && valueCodes.has(code) && mapped !== idCode && !out[idCode]) {
        out[idCode] = { ...entry };
      }
    } else if (converts && !valueCodes.has(code)) {
      out[idCode] = { ...entry };
    } else if (converts) {
      out[code] = { ...entry };
      if (!out[idCode]) out[idCode] = { ...entry };
    } else {
      out[code] = { ...entry };
    }
  }
  return out;
}

function remapOntologyNodeIds(
  ontology: Record<string, unknown> | undefined,
  nodeIds: Set<string>,
  valueCodes: Set<string>,
  coding?: Adl2CodingOptions,
): Record<string, unknown> | undefined {
  if (!ontology) return ontology;
  const table = ontology.term_definitions as Record<string, TermBag> | undefined;
  if (!table) return ontology;
  const term_definitions: Record<string, TermBag> = {};
  for (const [lang, bag] of Object.entries(table)) {
    term_definitions[lang] = remapTermBag(bag, nodeIds, valueCodes, coding);
  }
  return { ...ontology, term_definitions };
}

function remapComponentTerminologies(
  index: OperationalTemplateWithTermScopes["archetype_term_definitions"],
  nodeIds: Set<string>,
  valueCodes: Set<string>,
  coding?: Adl2CodingOptions,
): OperationalTemplateWithTermScopes["archetype_term_definitions"] {
  if (!index) return index;
  const out: NonNullable<typeof index> = {};
  for (const [archId, byLang] of Object.entries(index)) {
    const langs: Record<string, TermBag> = {};
    for (const [lang, bag] of Object.entries(byLang)) {
      langs[lang] = remapTermBag(bag, nodeIds, valueCodes, coding);
    }
    out[archId] = langs;
  }
  return out;
}

/** Byte / line size comparison for OPT XML vs OPT2 exports. */
export function opt2SizeMetrics(
  xmlOrOther: string,
  opt2: string,
): {
  xmlBytes: number;
  opt2Bytes: number;
  byteRatio: number;
  xmlLines: number;
  opt2Lines: number;
  lineRatio: number;
} {
  const xmlBytes = new TextEncoder().encode(xmlOrOther).length;
  const opt2Bytes = new TextEncoder().encode(opt2).length;
  const xmlLines = xmlOrOther.split("\n").length;
  const opt2Lines = opt2.split("\n").length;
  return {
    xmlBytes,
    opt2Bytes,
    byteRatio: xmlBytes / Math.max(opt2Bytes, 1),
    xmlLines,
    opt2Lines,
    lineRatio: xmlLines / Math.max(opt2Lines, 1),
  };
}

/** An id the ADL2 tokenizer reads as one token: invalid characters become `_`. */
export function adl2IdText(id: string): string {
  const text = id.trim().replace(/[^A-Za-z0-9_.-]+/g, "_");
  return /^[A-Za-z]/.test(text) ? text : `t_${text}`;
}
